import PropTypes from "prop-types"
import React from "react"
import * as amplitude from "@amplitude/analytics-browser"
import i18n from "i18n"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { saveAs } from "file-saver"
import CircularProgress from "@mui/material/CircularProgress"

import { useProcess } from "hooks/useProcess"

const ModalWaitProcess = ({ open, setOpen, PK, SK }) => {
  const [process] = useProcess(PK, SK)

  const handleDownload = url => {
    const basename = process?.payload?.filename
      ? process.payload.filename
      : "documento.pdf"
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        saveAs(blob, basename)
      })
    handleClose()
  }

  const handleClose = () => {
    setOpen(false)
  }

  const descriptionText = (() => {
    if (!process) return i18n.t("Estamos construyendo el documento.")
    if (process.status === "PENDING" || process.status === "PROCESSING")
      return i18n.t("Estamos construyendo el documento.")
    if (process.status === "DONE") return i18n.t("¡El documento está listo!")
    if (process.status === "ERROR")
      return i18n.t("Ocurrió un error al construir el documento.")
    return i18n.t("Estamos construyendo el documento.")
  })()

  const hasDownload = !!process?.payload?.download_url
  const chaptersDone = process?.payload?.chapters_done ?? 0
  const chaptersTotal = process?.payload?.chapters_total ?? 0
  const partsTotal = process?.payload?.parts_total ?? 0
  const partsDone = Math.min(process?.payload?.parts_done ?? 0, partsTotal)

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ "data-testid": "pdf-wait-process-modal" }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        {i18n.t("Descarga PDF")}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          data-testid="pdf-wait-process-close-button"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          py: 2,
        }}
      >
        <Typography sx={{ fontWeight: 300, fontSize: 14, textAlign: "center" }}>
          {descriptionText}
        </Typography>
        {!hasDownload && (
          <Box
            sx={{ display: "flex", justifyContent: "center", width: "100%" }}
          >
            <CircularProgress variant="indeterminate" />
          </Box>
        )}

        {chaptersTotal ? (
          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ fontWeight: 300, fontSize: 14 }}>
              {i18n.t("Escritos")} {chaptersDone}/{chaptersTotal}{" "}
              {i18n.t("capítulos")}
            </Typography>
            <Typography sx={{ fontWeight: 300, fontSize: 14 }}>
              {i18n.t("Escritas")} {partsDone}/{partsTotal} {i18n.t("partidas")}
            </Typography>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: "center" }}>
        {hasDownload && (
          <Button
            color="branded"
            variant="contained"
            data-testid="pdf-wait-process-download-button"
            onClick={() => {
              amplitude.track(process.process_type)
              handleDownload(process.payload.download_url)
            }}
          >
            {i18n.t("Descargar")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

ModalWaitProcess.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  PK: PropTypes.string.isRequired,
  SK: PropTypes.string.isRequired,
}

export default ModalWaitProcess
